import React from "react";
import { loadGeotabDay } from "../../hooks/useGeotabDay.js";
import { tourGpsDay, tourGpsProof } from "../../utils/billingInvoiceGps.js";
import { Block } from "../consolidation/GeotabSheetGantt.jsx";
import { fmtDistance } from "../geotab/geotabFormat.js";

export const GPS_PANEL_ID = "invoice-gps-proof";
const AXIS = 34, GAP = 8, HEAD = 24, MIN_WIDTH = 262;
const ROAD = ["#475569", "#fff"], GPS_ROAD = ["#334155", "#fff"], MISSING = ["#fff", "#c5524a"];
const clock = (minutes) => {
  if (minutes == null) return "—";
  const rounded = Math.round(minutes);
  return `${Math.floor(rounded / 60) % 24} h ${String(((rounded % 60) + 60) % 60).padStart(2, "0")}`;
};
const span = (from, to) => `${clock(from)} → ${clock(to)}`;
const dayLabel = (value) => new Intl.DateTimeFormat("fr-CA", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(`${value}T12:00:00Z`));

/** Geotab days of every tour, read once at opening (cache shared with Consolidation), and the tour shown on the right. */
export function useInvoiceGps(view, allowed) {
  const tours = React.useMemo(() => view.allDays.flatMap((day) => day.tours), [view.allDays]);
  const key = allowed ? [...new Set(tours.map((tour) => tourGpsDay(tour.source)).filter(Boolean))].join("|") : "";
  const [loaded, setLoaded] = React.useState({ key: "", data: {} });
  const [selected, setSelected] = React.useState(null);
  React.useEffect(() => {
    if (!key) return undefined;
    let live = true;
    const days = key.split("|");
    Promise.allSettled(days.map((day) => loadGeotabDay(...day.split("@")))).then((results) => {
      if (live) setLoaded({ key, data: Object.fromEntries(days.map((day, index) => [day, results[index].value || null])) });
    });
    return () => { live = false; };
  }, [key]);
  const ready = Boolean(key) && loaded.key === key;
  const proofs = React.useMemo(() => new Map(tours.map((tour) => [tour.key,
    ready ? tourGpsProof(tour.source, loaded.data[tourGpsDay(tour.source)]) : undefined])), [tours, ready, loaded]);
  const select = React.useCallback((tourKey, reveal = false) => {
    setSelected(tourKey);
    // Below the table on a narrow screen: bring the proof into view (the right column is always in view).
    if (reveal && !window.matchMedia("(min-width: 1280px)").matches) {
      requestAnimationFrame(() => document.getElementById(GPS_PANEL_ID)?.scrollIntoView({ block: "nearest", behavior: "smooth" }));
    }
  }, []);
  const checked = tours.filter((tour) => tourGpsDay(tour.source));
  const selectedTour = allowed && view.days.some((day) => view.isDayOpen(day.key) && day.tours.some((tour) => tour.key === selected))
    ? tours.find((tour) => tour.key === selected) || null : null;
  React.useEffect(() => {
    if (selected && !selectedTour) setSelected(null);
  }, [selected, selectedTour]);
  return { allowed, enabled: Boolean(key), ready, proofs, select, selected: selectedTour,
    summary: { total: checked.length, ok: checked.filter((tour) => proofs.get(tour.key)?.ok).length } };
}

// Billed boxes next to the Geotab boxes, as in Consolidation (« TransIA vs Geotab »).
function GpsTimeline({ proof }) {
  const containerRef = React.useRef(null);
  const [width, setWidth] = React.useState(MIN_WIDTH);
  React.useEffect(() => {
    const container = containerRef.current;
    const measure = () => setWidth(Math.max(MIN_WIDTH, container.clientWidth));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const WIDTH = width, LANE = (WIDTH - AXIS - 4 - GAP) / 2;
  const BILLED = AXIS + 4, REAL = BILLED + LANE + GAP;
  const times = proof.stops.flatMap((stop) => [...Object.values(stop.billed), ...Object.values(stop.real || {})]).filter((value) => value != null);
  const start = Math.floor((Math.min(...times) - 10) / 30) * 30, end = Math.ceil((Math.max(...times) + 10) / 30) * 30;
  const scale = Math.min(2, Math.max(0.8, 400 / (end - start)));
  const y = (minutes) => 6 + (minutes - start) * scale;
  const height = y(end) + 6;
  const hours = [];
  for (let minutes = Math.ceil(start / 60) * 60; minutes <= end; minutes += 60) hours.push(minutes);
  return <div ref={containerRef} className="overflow-x-auto">
    <div className="relative" style={{ width: WIDTH, height: HEAD + height }}>
      <div className="absolute inset-x-0 top-0 h-6 border-b border-slate-200 text-[10.5px] font-bold text-slate-700">
        <span className="absolute flex h-6 items-center justify-center" style={{ left: BILLED, width: LANE }}>Facturé</span>
        <span className="absolute flex h-6 items-center justify-center" style={{ left: REAL, width: LANE }}><img src="/brand/geotab-logo.svg" alt="Geotab" className="h-3.5 object-contain" style={{ width: 76 }} /></span>
      </div>
      <svg width={WIDTH} height={height} className="absolute block select-none" style={{ top: HEAD }} role="img" aria-label="Heures facturées et Geotab">
        {hours.map((minutes) => <g key={minutes}>
          <line x1={AXIS + 3} y1={y(minutes)} x2={WIDTH} y2={y(minutes)} stroke="#eef2f6" />
          <text x={AXIS} y={y(minutes) + 3.5} fontSize={10} fill="#1e293b" textAnchor="end">{Math.floor(minutes / 60) % 24} h</text>
        </g>)}
        {proof.events.filter((event) => event.billed != null && event.real != null).map((event) =>
          <line key={event.key} x1={BILLED + LANE} y1={y(event.billed)} x2={REAL} y2={y(event.real)} stroke="#94a3b8" strokeWidth={1.5} strokeDasharray="4 3" />)}
        {proof.stops.map(({ key, kind, name, billed, real, offset }) => <React.Fragment key={key}>
          <Block x={BILLED} lane={LANE} top={y(billed.start)} bottom={y(billed.arrival ?? billed.start)} name={["Route", span(billed.start, billed.arrival)]} colors={ROAD}
            hover={{ title: `Route → ${name}`, rows: [["Facturé", span(billed.start, billed.arrival)], ["Geotab", real ? span(real.start, real.arrival) : "non trouvé"]] }} />
          <Block x={BILLED} lane={LANE} top={y(billed.arrival ?? billed.start)} bottom={y(billed.end)} kind={kind} name={[name, span(billed.arrival, billed.end)]}
            hover={{ title: name, rows: [["Facturé", span(billed.arrival, billed.end)], ["Geotab", real ? span(real.arrival, real.end) : "non trouvé"]] }} />
          {real ? <>
            <Block x={REAL} lane={LANE} top={y(real.start)} bottom={y(real.arrival)} name={["En route", span(real.start, real.arrival)]} colors={GPS_ROAD}
              hover={{ title: "Route Geotab", rows: [["Trajet", span(real.start, real.arrival)]] }} />
            <Block x={REAL} lane={LANE} top={y(real.arrival)} bottom={y(real.end ?? end)} kind={kind} name={[`Arrêt ${name}`, span(real.arrival, real.end)]}
              hover={{ title: `Arrêt Geotab · ${name}`, rows: [["Arrêt", span(real.arrival, real.end)], offset != null && ["Distance", `${fmtDistance(offset)} du site`]] }} />
          </> : <Block x={REAL} lane={LANE} top={y(billed.start)} bottom={y(billed.end)} name={["Non trouvé", "dans Geotab"]} colors={MISSING} dashed stroke="#c5524a"
            hover={{ title: name, rows: [["Geotab", "Aucun arrêt pour ces commandes"]] }} />}
        </React.Fragment>)}
      </svg>
    </div>
  </div>;
}

/** « Preuve GPS » of the chosen tour: right column from 1 280 px, under the table on a narrow screen. */
export default function InvoiceGpsPanel({ gps }) {
  const tour = gps.selected;
  if (!gps.enabled || !tour) return null;
  const proof = gps.proofs.get(tour.key);
  return <section id={GPS_PANEL_ID} aria-label="Preuve GPS" className="mt-3 scroll-mt-2 rounded-[4px] border border-slate-300 bg-white p-2.5 text-xs text-slate-800 xl:sticky xl:top-0 xl:mt-0 xl:max-h-[calc(100dvh-8rem)] xl:overflow-y-auto">
    <header className="mb-1 flex flex-wrap items-baseline gap-x-2">
      <h3 className="text-[13px] font-extrabold text-slate-900">{dayLabel(tour.source.date)}</h3>
      <span className="ml-auto font-bold text-slate-500">{[tour.driver, tour.truck].filter(Boolean).join(" · ")}</span>
    </header>
    {proof === undefined ? <p className="text-slate-500">Lecture de Geotab…</p>
      : !proof ? <p className="text-slate-500">Geotab est indisponible pour cette journée.</p>
        : !proof.total ? <p className="text-slate-500">Aucun trajet Geotab pour ce camion ce jour-là.</p>
          : <>
            <GpsTimeline proof={proof} />
            <dl className="mt-2 grid grid-cols-[minmax(0,1fr)_auto_auto] gap-x-3 gap-y-0.5 border-t border-slate-200 pt-1.5 tabular-nums">
              <dt className="text-[10px] font-bold text-slate-500">Heure facturée</dt><dd className="m-0 text-[10px] font-bold text-slate-500">Facturé</dd><dd className="m-0 text-[10px] font-bold text-slate-500">Geotab</dd>
              {proof.events.map((event) => <React.Fragment key={event.key}>
                <dt className="truncate">{event.label}</dt>
                <dd className="m-0 font-extrabold">{clock(event.billed)}</dd>
                <dd className={`m-0 font-bold ${event.ok ? "text-accent" : "text-[#c5524a]"}`}>{clock(event.real)} {event.ok ? "✓" : ""}</dd>
              </React.Fragment>)}
            </dl>
            <p className="mt-1.5 text-[11px] text-slate-500">La route est facturée du départ à l’arrivée ; les arrêts en route ne sont pas contrôlés.</p>
          </>}
  </section>;
}
